import { z } from 'zod';
import {
  type CreateSquadInput,
  type ProjectId,
  ProjectIdSchema,
  type Squad,
  type SquadId,
  SquadSchema,
  type UpdateSquadInput,
} from '@voll-intelligence/domain';
import type { HttpService } from '@web/data/services';

const ListSquadsResponseSchema = z.object({ squads: z.array(SquadSchema) });
const SquadResponseSchema = z.object({ squad: SquadSchema });

/**
 * `GET /orgs/$orgId/squads/$squadId` response. The detail endpoint
 * carries `projectId` so the flattened `/squad/$squadId/...` routes
 * can resolve the parent project without keeping it in the URL.
 */
const SquadDetailResponseSchema = z.object({
  squad: SquadSchema.extend({ projectId: ProjectIdSchema }),
});

interface SquadDetail extends Squad {
  projectId: ProjectId;
}

interface SquadsRepository {
  listSquads: (orgId: string, projectId: ProjectId) => Promise<Squad[]>;
  getSquad: (orgId: string, projectId: ProjectId, squadId: SquadId) => Promise<Squad>;
  /** Fetches a squad by id alone. Used by the flattened squad routes. */
  getSquadById: (orgId: string, squadId: SquadId) => Promise<SquadDetail>;
  createSquad: (orgId: string, projectId: ProjectId, input: CreateSquadInput) => Promise<Squad>;
  updateSquad: (
    orgId: string,
    projectId: ProjectId,
    squadId: SquadId,
    input: UpdateSquadInput,
  ) => Promise<Squad>;
}

export function createSquadsRepository(httpService: HttpService): SquadsRepository {
  async function listSquads(orgId: string, projectId: ProjectId): Promise<Squad[]> {
    const { squads } = await httpService.get(`/orgs/${orgId}/projects/${projectId}/squads`, {
      schema: ListSquadsResponseSchema,
    });
    return squads;
  }

  async function getSquad(orgId: string, projectId: ProjectId, squadId: SquadId): Promise<Squad> {
    const squads = await listSquads(orgId, projectId);
    const squad = squads.find((s) => s.id === squadId);
    if (!squad) throw new Error('Squad not found.');
    return squad;
  }

  async function getSquadById(orgId: string, squadId: SquadId): Promise<SquadDetail> {
    const { squad } = await httpService.get(`/orgs/${orgId}/squads/${squadId}`, {
      schema: SquadDetailResponseSchema,
    });
    return squad;
  }

  async function createSquad(
    orgId: string,
    projectId: ProjectId,
    input: CreateSquadInput,
  ): Promise<Squad> {
    const { squad } = await httpService.post(`/orgs/${orgId}/projects/${projectId}/squads`, input, {
      schema: SquadResponseSchema,
    });
    return squad;
  }

  async function updateSquad(
    orgId: string,
    projectId: ProjectId,
    squadId: SquadId,
    input: UpdateSquadInput,
  ): Promise<Squad> {
    const { squad } = await httpService.patch(
      `/orgs/${orgId}/projects/${projectId}/squads/${squadId}`,
      input,
      { schema: SquadResponseSchema },
    );
    return squad;
  }

  return { listSquads, getSquad, getSquadById, createSquad, updateSquad };
}
